"use client"
import { Box, Button, Typography, Breadcrumbs, Link, useTheme } from "@mui/material"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { NavigateNext as NavigateNextIcon } from "@mui/icons-material"

const PageHeader = ({
  title,
  subtitle,
  action,
  actionText,
  actionIcon,
  actionColor = "primary",
  onActionClick,
  backButton,
}) => {
  const theme = useTheme()
  const location = useLocation()

  // Generate breadcrumbs from the current path
  const generateBreadcrumbs = () => {
    const paths = location.pathname.split("/").filter((path) => path)

    // Don't show breadcrumbs if we're at the dashboard
    if (paths.length === 1 && paths[0] === "dashboard") {
      return null
    }

    return (
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
          Dashboard
        </Link>

        {paths.map((path, index) => {
          // Skip 'dashboard' as it's already included
          if (path === "dashboard") return null

          const routeTo = `/${paths.slice(0, index + 1).join("/")}`
          const isLast = index === paths.length - 1

          // Format the path for display (capitalize, replace hyphens)
          const formattedPath = path.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())

          return isLast ? (
            <Typography key={path} color="textPrimary">
              {formattedPath}
            </Typography>
          ) : (
            <Link key={path} component={RouterLink} to={routeTo} color="inherit" underline="hover">
              {formattedPath}
            </Link>
          )
        })}
      </Breadcrumbs>
    )
  }

  return (
    <Box sx={{ mb: 4 }}>
      {generateBreadcrumbs()}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          {backButton && <Box sx={{ mb: 2 }}>{backButton}</Box>}

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                  : "inherit",
              WebkitBackgroundClip: theme.palette.mode === "dark" ? "text" : "inherit",
              WebkitTextFillColor: theme.palette.mode === "dark" ? "transparent" : "inherit",
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {action && (
          <Box>
            {actionText ? (
              <Button variant="contained" color={actionColor} startIcon={actionIcon} onClick={onActionClick}>
                {actionText}
              </Button>
            ) : (
              action
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default PageHeader

